// ─── API envelope ──────────────────────────────────────────────────────────
export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface ApiResponseMeta {
  timestamp?: string;
  pagination?: PaginationMeta;
}

export interface ApiResponse<T> {
  data: T;
  meta: ApiResponseMeta;
}

export interface ApiError {
  error: {
    code?: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// ─── Auth ──────────────────────────────────────────────────────────────────
export type Role = 'SUPER_ADMIN' | 'ADMIN' | 'PROVIDER' | 'CUSTOMER';

export interface AuthUser {
  id: string;
  email: string;
  /** Public privacy handle (V49) — what other users see in conversations,
   *  never the email. Always present on responses from a current backend. */
  username?: string;
  /** Optional since the simplified signup lets users register with email +
   *  password only. UI should call {@link userDisplayName} to render a name
   *  with an email-local-part fallback when both first/last names are null. */
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  roles: Role[];
  permissions: string[];
}

/** Mirrors the backend's {@code User#getDisplayName()} — best-effort handle:
 *  full name → first → last → email's local-part. Always returns a non-empty
 *  string for a valid {@link AuthUser}. */
export function userDisplayName(u: Pick<AuthUser, 'firstName' | 'lastName' | 'email'>): string {
  const first = u.firstName?.trim();
  const last = u.lastName?.trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  const at = u.email?.indexOf('@') ?? -1;
  return at > 0 ? u.email.substring(0, at) : (u.email ?? '');
}

/** Same fallback as {@link userDisplayName} but returns up to two-letter
 *  initials for avatars. */
export function userInitials(u: Pick<AuthUser, 'firstName' | 'lastName' | 'email'>): string {
  const first = u.firstName?.trim() ?? '';
  const last = u.lastName?.trim() ?? '';
  const initials = `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
  if (initials) return initials;
  const handle = userDisplayName(u);
  return handle.substring(0, 2).toUpperCase() || '?';
}

export interface VerificationStatus {
  emailVerified: boolean;
  phoneVerified: boolean;
  fullyVerified: boolean;
  phoneNumber?: string | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

// ─── AfroEspace domain ─────────────────────────────────────────────────────
export interface Country {
  id: string;
  alpha2: string;          // 'DE'
  alpha3: string;          // 'DEU'
  name: string;            // localised display name
  dialCode?: string | null;
  timezoneDefault?: string | null;
  defaultLocale: string;
  defaultCurrency: string;
  displayOrder: number;
}

export interface AdminRegion {
  id: string;
  code: string;
  name: string;            // localised display name
  displayOrder: number;
}

export interface City {
  id: string;
  name: string;
  country: string;
  region?: string | null;
  slug: string;
}

export interface Category {
  id: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
}

export interface ServiceCatalog {
  id: string;
  categoryId: string;
  categoryCode: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
}

export type ProviderStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';

/** Items returned by GET /providers/me/completeness — drives the wizard ring. */
export interface CompletenessItem {
  key:
    | 'BUSINESS_NAME'
    | 'CITY'
    | 'CONTACT'
    | 'BIO'
    | 'SERVICES'
    | 'MULTIPLE_SERVICES'
    | 'PORTFOLIO'
    | 'RICH_PORTFOLIO';
  done: boolean;
  weight: number;
}

export interface Completeness {
  score: number;             // 0–100
  readyToPublish: boolean;   // server-side threshold (currently 60 %)
  items: CompletenessItem[];
}

export interface CityRef {
  id: string;
  name: string;
  country: string;
  slug: string;
}

export interface ProviderSummary {
  id: string;
  businessName: string;
  city: CityRef;
  coverImageUrl?: string | null;
  portfolioCount: number;
  serviceCount: number;
  /** Average rating from {@code provider_reviews}, 0 when no review yet. */
  ratingAvg?: number | null;
  ratingCount?: number | null;
  /**
   * V48 service coverage. {@code true} when this hit is a travelling
   * provider based in another city (rendered "Based in X · Travels to Y"),
   * {@code false}/absent for a true local provider. {@link distanceKm} is
   * the base→searched-city distance, present only for travel hits.
   */
  travelProvider?: boolean;
  distanceKm?: number | null;
}

/**
 * How {@link ProviderServiceItem.customPriceCents} should be presented:
 *   • FIXED         — render plain (e.g. "80 €")
 *   • STARTING_FROM — prefix with the localized "From " (e.g. "From 80 €")
 * Null when the row has no price at all.
 */
export type PriceType = 'FIXED' | 'STARTING_FROM';

export interface ProviderServiceItem {
  id: string;
  /** Null for provider-defined custom services. */
  serviceCatalogId: string | null;
  /** Always present (either from the catalog entry or the row's category). */
  categoryCode: string | null;
  /** Null for custom services. */
  serviceCode: string | null;
  /** Catalog name when serviceCatalogId is set, otherwise the customName. */
  serviceName: string;
  customPriceCents?: number | null;
  currency?: string | null;
  priceType?: PriceType | null;
  durationMinutes?: number | null;
  description?: string | null;
}

export interface PortfolioImage {
  id: string;
  imageUrl: string;
  caption?: string | null;
  displayOrder: number;
  /** Optional per-piece price in minor units (cents). Null = "on request". */
  priceCents?: number | null;
  /** ISO-4217 alphabetic currency code, present iff priceCents is set. */
  currency?: string | null;
  createdAt: string;
}

export interface EventSpecialization {
  id: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
}

/** Lightweight reference of an event specialization attached to a provider. */
export interface EventSpecializationRef {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
}

/**
 * Social-media links a provider has chosen to expose on their shop.
 * Every field is nullable — the public profile only renders icons for
 * the platforms the provider has filled in. URLs are stored verbatim
 * (no per-platform parsing) so the UI can render anchors directly.
 */
export interface SocialLinks {
  instagram?: string | null;
  tiktok?: string | null;
  facebook?: string | null;
  snapchat?: string | null;
  youtube?: string | null;
  twitter?: string | null;
}

export interface ProviderDetail {
  id: string;
  businessName: string;
  bio?: string | null;
  /**
   * Legacy reference to the seeded `cities` table.  Optional on rows
   * created via the country-state-city picker.  Prefer the denormalised
   * fields below.
   */
  city?: CityRef | null;
  /** ISO-3166-1 alpha-2 country code from the country-state-city picker. */
  countryCode?: string | null;
  countryName?: string | null;
  stateCode?: string | null;
  stateName?: string | null;
  cityName?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  whatsappNumber?: string | null;
  whatsappUrl?: string | null;
  status: ProviderStatus;
  createdAt: string;
  ratingAvg?: number | null;
  ratingCount?: number | null;
  services: ProviderServiceItem[];
  portfolio: PortfolioImage[];
  /** Event types this provider supports (Wedding, Birthday, ...). */
  eventSpecializations: EventSpecializationRef[];
  /** Optional — backend always returns a SocialLinks object; entries
   *  inside it may be null when the provider hasn't filled them in. */
  social?: SocialLinks | null;
  /**
   * V48 service coverage. {@code canTravel} opts the provider into
   * out-of-city jobs; {@code travelRadiusKm} is how far (km) from the base
   * city they will go. Optional so older API responses still type-check.
   */
  canTravel?: boolean;
  travelRadiusKm?: number | null;
}

export interface ProviderMine extends ProviderDetail {
  rejectedReason?: string | null;
  approvedAt?: string | null;
}

// ─── Weekly opening hours ──────────────────────────────────────────────────
/**
 * Day-of-week numbering used end-to-end (matches the backend):
 *   0 = Sunday … 6 = Saturday.
 * Times are local wall-clock "HH:mm" strings. Closed days set `isClosed=true`
 * and leave both times null.
 */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface WeeklyHoursDay {
  dayOfWeek: DayOfWeek;
  opensAt: string | null;
  closesAt: string | null;
  isClosed: boolean;
}

export interface WeeklyHours {
  days: WeeklyHoursDay[];
}
